import { useState } from "react";
import { Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { draftRecord } from "@/lib/aiStudio";
import LogoPicker from "@/components/admin/LogoPicker";

const DRAFTABLE = ["text", "loc", "locarea", "textarea"];

const HINTS = {
  sectors: "e.g. new sector: water desalination and treatment in the UAE, two plants under study, positioned as national infrastructure",
  ventures: "e.g. JV signed March 2026 with a Spanish rail contractor, Abu Dhabi, focus on metro depot construction",
  leaders: "e.g. new board member, 20 years in Gulf project finance, previously at a sovereign fund, chairs the audit committee",
  posts: "e.g. announcement: first production line commissioned at the Gypto Alqalaa Pharma complex, Al Obour, March 2026",
  locations: "e.g. a new steel service centre in Dubai Industrial City, opening 2027",
};

/**
 * "Describe it and I'll write it" — fills every text field on the record at once.
 */
export default function AiDraftPanel({ kind, fields, item, onApply, testKey }) {
  const [open, setOpen] = useState(false);
  const [brief, setBrief] = useState("");
  const [busy, setBusy] = useState(false);
  const [showLogos, setShowLogos] = useState(false);

  const draftable = fields.filter((f) => DRAFTABLE.includes(f.type || "text") && f.key !== "order");
  const imageField = fields.find((f) => ["image", "media", "photo_url"].includes(f.type) || f.key === "image");
  const imageKey = imageField?.key;
  const brandGuess = item?.name?.en || item?.name || item?.partner || "";

  const run = async () => {
    if (!brief.trim()) {
      toast.error("Describe what you want written first.");
      return;
    }
    setBusy(true);
    try {
      const draft = await draftRecord({
        kind,
        brief,
        fields: draftable.map((f) => ({ key: f.key, label: f.label, type: f.type || "text" })),
      });
      const patch = {};
      for (const f of draftable) {
        const val = draft[f.key];
        if (val == null) continue;
        if (f.type === "loc" || f.type === "locarea") {
          if (typeof val === "object") patch[f.key] = { en: val.en || "", ar: val.ar || "" };
        } else if (typeof val === "string") {
          patch[f.key] = val;
        }
      }
      if (!Object.keys(patch).length) throw new Error("The draft came back empty — try a fuller brief.");
      onApply(patch);
      toast.success("Draft written in — review it, then Save");
      setBrief("");
      if (imageKey) setShowLogos(true);
      else setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={`ai-draft-open-${testKey}`}
        className="inline-flex items-center gap-2 rounded-full border border-primary/50 bg-primary/5 px-4 py-2 text-xs font-semibold text-primary transition-colors hover:bg-primary/10 md:col-span-2"
      >
        <Sparkles className="h-3.5 w-3.5" /> Draft this with AI
      </button>
    );
  }

  return (
    <div
      className="border border-primary/40 bg-primary/5 p-4 md:col-span-2"
      style={{ borderRadius: "var(--radius)" }}
      data-testid={`ai-draft-panel-${testKey}`}
    >
      <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-primary">
        <Sparkles className="h-3.5 w-3.5" /> Draft with AI
      </p>
      <p className="mt-2 text-xs text-muted-foreground">
        Describe it in your own words. The assistant writes every English and Arabic field below in the
        group's house tone — nothing is published until you press Save.
      </p>
      <Textarea
        rows={3}
        className="mt-3"
        value={brief}
        placeholder={HINTS[kind] || "Describe what you want written…"}
        data-testid={`ai-draft-brief-${testKey}`}
        onChange={(e) => setBrief(e.target.value)}
      />
      <div className="mt-3 flex items-center gap-3">
        <button
          type="button"
          onClick={run}
          disabled={busy}
          data-testid={`ai-draft-run-${testKey}`}
          className="btn-gold h-10 px-5 text-xs disabled:opacity-50"
        >
          {busy ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Writing…
            </span>
          ) : (
            "Write the draft"
          )}
        </button>
        {imageKey && !showLogos && (
          <button
            type="button"
            onClick={() => setShowLogos(true)}
            disabled={busy}
            data-testid={`ai-draft-logos-${testKey}`}
            className="text-xs font-semibold text-primary hover:underline"
          >
            Find a logo instead
          </button>
        )}
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            setShowLogos(false);
          }}
          disabled={busy}
          data-testid={`ai-draft-cancel-${testKey}`}
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          Close
        </button>
      </div>

      {imageKey && showLogos && (
        <div className="mt-4">
          <LogoPicker
            brand={brandGuess}
            testKey={testKey}
            onPick={(url) => onApply({ [imageKey]: url })}
          />
        </div>
      )}
    </div>
  );
}
